/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Stack, Tab, Tabs, Tooltip, Typography, alpha } from '@mui/material';
import { DashboardCustomize, Edit, ExpandMore, LibraryBooks, Pageview, People } from '@mui/icons-material';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Member } from 'src/modules/member/models/member';
import { MemberDetailsSummary } from 'src/modules/member/components/memberDetailsSummary';
import { getGroupMessageEmailTemplateById } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { isDesignModeCompatible } from '../builder/parse';
import { EmailBuilderModal, EmailBuilderMode } from './emailBuilderModal';
import { EmailTemplatePickerModal } from './emailTemplatePickerModal';
import { GroupMessageEmailTemplateView } from './groupMessageEmailTemplateView';
import { GroupMessageEmailTemplate } from '../models/groupMessageEmailTemplate';
import { RecipientList } from './recipientList';

type ComposeEmailFormProps = {
  members: Member[];
  disabled?: boolean;
};

// Basic-mallens systemmall-id (negativa id:n = systemmallar, se EmailTemplateProvider.cs).
const DEFAULT_TEMPLATE_ID = -10;
const DEFAULT_TEMPLATE_LABEL = 'Basic';

export const ComposeEmailForm = (props: ComposeEmailFormProps) => {
  const { members, disabled = false } = props;
  const [membersWithEmail, setMembersWithEmail] = useState<Member[]>([]);
  const [membersWithoutEmail, setMembersWithoutEmail] = useState<Member[]>([]);
  const { t } = useTranslation(['groupMessage', 'common']);
  const { watch, setValue } = useFormContext();
  const isMounted = useIsMounted();

  // Mall-vy: tabs (Redigera/Förhandsgranska) och designläge — speglar create/edit-sidans
  // pattern. Designläge-knappen disablas om body inte har data-email-doc-marker.
  const [previewTab, setPreviewTab] = useState<'preview' | 'edit'>('edit');
  const [builderMode, setBuilderMode] = useState<EmailBuilderMode | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState<string>(DEFAULT_TEMPLATE_LABEL);
  const body = watch('body');
  const designModeAvailable = isDesignModeCompatible(body);
  const effectivePreviewTab = designModeAvailable ? 'preview' : previewTab;

  useEffect(() => {
    setMembersWithEmail(members.filter((x) => !!x.email));
    setMembersWithoutEmail(members.filter((x) => !x.email));
  }, [members]);

  // Ladda Basic-mallen som default när formuläret öppnas (om body är tom).
  useEffect(() => {
    if (body && body.length > 0) return;
    (async () => {
      try {
        const res = await getGroupMessageEmailTemplateById(DEFAULT_TEMPLATE_ID);
        if (!isMounted() || !res?.data) return;
        // Endpoint returnerar antingen rå HTML-sträng eller ett objekt med .body — hantera båda.
        const html = typeof res.data === 'string' ? res.data : (res.data as GroupMessageEmailTemplate).body;
        if (html) setValue('body', html, { shouldDirty: false });
      } catch (e) {
        ConsoleHelper.log(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textEditorInitalized = useCallback(() => {
    return null;
  }, []);

  const handleTemplatePick = (template: GroupMessageEmailTemplate) => {
    setValue('body', template.body, { shouldDirty: true });
    setCurrentTemplateName(template.name);
    // Vid valet av en design-mall → gå till preview-läget direkt, annars edit.
    setPreviewTab(isDesignModeCompatible(template.body) ? 'preview' : 'edit');
  };

  return (
    <>
      {membersWithEmail.length > 0 && (
        <Accordion
          defaultExpanded={membersWithEmail.length <= 1}
          disableGutters
          elevation={0}
          sx={{
            m: 1,
            mb: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <People fontSize='small' aria-hidden />
              <Typography variant='subtitle2'>
                {t('groupMessage:selection.recipients')} <strong>({membersWithEmail.length})</strong>
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {membersWithEmail.length === 1 && <MemberDetailsSummary member={membersWithEmail[0]} />}
            {membersWithEmail.length > 1 && <RecipientList members={membersWithEmail} />}
          </AccordionDetails>
        </Accordion>
      )}
      {membersWithoutEmail.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            m: 1,
            mb: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <People fontSize='small' aria-hidden />
              <Typography variant='subtitle2'>
                {t('groupMessage:selection.recipientsWithoutEmail')} <strong>({membersWithoutEmail.length})</strong>
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <RecipientList members={membersWithoutEmail} />
          </AccordionDetails>
        </Accordion>
      )}
      {membersWithEmail.length > 0 && (
        <Box sx={{ p: 1 }}>
          <GridWrapper>
            <Grid item xs={12}>
              <ControlledTextField
                name='subject'
                required
                disabled={disabled}
                label={t('groupMessage:formFields.subject')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name='senderTitle'
                required
                disabled={disabled}
                label={t('groupMessage:formFields.senderTitle')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name='sender'
                required
                disabled={disabled}
                label={t('groupMessage:formFields.sender')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 1,
                  py: 0.5,
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{t('groupMessage:create.templateLabel')}:</strong> {currentTemplateName}
                    </Typography>
                    <Button
                      variant='text'
                      size='small'
                      startIcon={<LibraryBooks fontSize='small' />}
                      onClick={() => setTemplatePickerOpen(true)}
                      disabled={disabled}
                      sx={{ textTransform: 'none', flexShrink: 0, py: 0 }}
                    >
                      {t('groupMessage:create.changeTemplate')}
                    </Button>
                  </Stack>
                  <Tabs
                    value={effectivePreviewTab}
                    onChange={(_, value: 'preview' | 'edit') => setPreviewTab(value)}
                    sx={{ minHeight: 'auto', ml: 'auto !important' }}
                  >
                    {!designModeAvailable && (
                      <Tab
                        value='edit'
                        label={t('groupMessage:sections.editContent')}
                        icon={<Edit fontSize='small' />}
                        iconPosition='start'
                        sx={{ minHeight: 36, py: 0 }}
                      />
                    )}
                    <Tab
                      value='preview'
                      label={t('groupMessage:builder.previewTab')}
                      icon={<Pageview fontSize='small' />}
                      iconPosition='start'
                      sx={{ minHeight: 36, py: 0 }}
                    />
                  </Tabs>
                </Stack>
                <Tooltip title={!designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<DashboardCustomize fontSize='small' />}
                      onClick={() => setBuilderMode('design')}
                      disabled={disabled || !designModeAvailable}
                    >
                      {t('groupMessage:builder.designMode')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {effectivePreviewTab === 'preview' && (
                <Box sx={{ pt: 2 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      boxShadow: 1,
                      transition: 'box-shadow 120ms ease',
                      '&:hover': {
                        boxShadow: 3,
                        '& .builder-overlay': {
                          opacity: 1,
                          pointerEvents: 'auto',
                        },
                      },
                    }}
                  >
                    <GroupMessageEmailTemplateView body={body || ''} height='30rem' />
                    {/* Hover-overlay med centrerade edit-knappar */}
                    <Box
                      className='builder-overlay'
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 150ms ease',
                      }}
                    >
                      <Stack direction='row' spacing={2}>
                        {!designModeAvailable && (
                          <Button
                            variant='contained'
                            size='large'
                            startIcon={<Edit />}
                            disableElevation
                            onClick={() => setPreviewTab('edit')}
                            sx={{ boxShadow: 4 }}
                          >
                            {t('groupMessage:sections.editContent')}
                          </Button>
                        )}
                        <Tooltip title={!designModeAvailable ? (t('groupMessage:builder.designModeLockedTooltip') as string) : ''}>
                          <span>
                            <Button
                              variant='contained'
                              size='large'
                              color='inherit'
                              startIcon={<DashboardCustomize />}
                              onClick={() => setBuilderMode('design')}
                              disabled={disabled || !designModeAvailable}
                              sx={{
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                boxShadow: 4,
                                '&:hover': { bgcolor: 'background.paper' },
                              }}
                            >
                              {t('groupMessage:builder.editInDesignMode')}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              )}
              {effectivePreviewTab === 'edit' && (
                <Box sx={{ pt: 2 }}>
                  <ControlledTextEditor name='body' noMargin initCallback={textEditorInitalized} disabled={disabled} rows={28} />
                </Box>
              )}
            </Grid>
          </GridWrapper>
        </Box>
      )}
      <EmailTemplatePickerModal open={templatePickerOpen} openModal={setTemplatePickerOpen} onTemplatePick={handleTemplatePick} />
      <EmailBuilderModal open={builderMode !== null} mode={builderMode ?? 'design'} onClose={() => setBuilderMode(null)} />
    </>
  );
};
